/**
 * How a Work Item's latest piece of work went. A Work Item's own status stays "open" (it's the
 * key repeat alerts collect onto), so the Runs tab shows this instead, from its newest firing.
 */
export type Outcome = 'Running' | 'Waiting for approval' | 'Success' | 'Failed';

type Item = {
  firings: { at: string | null; runs: string[] }[];
  approvals: { status: string; run_id: string | null }[];
  failures: { at: string; run: string | null }[];
};

/** `status` is a run's summary status, undefined while it loads (or from an older Harness
 * Service). Null when it can't tell yet. */
export function workOutcome(item: Item, status: (runId: string) => string | undefined): Outcome | null {
  const latest = item.firings.at(-1);
  const runs = latest?.runs ?? [];
  const statuses = runs.map(status);
  if (statuses.some((s) => s === 'running' || s === 'not started')) return 'Running';
  // An approval asked for by one of this firing's runs.
  if (item.approvals.some((a) => a.status === 'pending' && !!a.run_id && runs.includes(a.run_id))) return 'Waiting for approval';
  // A step that couldn't start: a hand-off from one of this firing's runs, or a delivery at or after
  // it (ISO UTC strings from the same server compare in order). With no `at`, older ones can't count.
  const ours = (f: Item['failures'][number]) => (f.run ? runs.includes(f.run) : !latest || (!!latest.at && f.at >= latest.at));
  if (item.failures.some(ours)) return 'Failed';
  if (statuses.some((s) => s === 'failed' || s === 'aborted')) return 'Failed';
  if (!statuses.length || statuses.includes(undefined)) return null;
  return 'Success';
}

/** Done: Watch becomes Logs. */
export const isDone = (o: Outcome | null) => o === 'Success' || o === 'Failed';
